import { Injectable, ElementRef } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import isEqual from 'lodash-es/isEqual';
import uniqWith from 'lodash-es/uniqWith';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TravelerPaxResponse, TravelerForm, TravelerPax, LoyaltyResponse, PaxDocumentResponse } from '@pax/entities/traveler-pax';
import { User } from '@app/modules/core/interfaces/user';
import { TravelerTypes, TravelerInfoType } from '@pax/entities/traveler-info';
import { AirValidationResponse } from '@app/entities/air-validation.response';
import { CountriesVm } from '@booking/entities/countries';
import { TRAVELER_FORM_INPUTS, INVALID } from '@booking/constants/forms.constants';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { PaxFormDocumentService } from '@pax/services/pax-form-document.service';
import { PaxFormLoyaltyService } from '@pax/services/pax-form-loyalty.service';
import { PaxFormDateService } from '@pax/services/pax-form-date.service';
import { PaxFormDataService } from '@pax/services/pax-form-data.service';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { FormErrors } from '@reservation/entities/form-errors';
import { BusinessRulesByBookingType, BookingPaxTypeDocumentProps } from '@app/entities/booking-documents';
import { BookingFormGeneralErrorTypes } from '@app/entities/booking-form-general-error';
import { FormDocumentInputComponent } from '@forms/form-components/components/form-document-input/form-document-input.component';
import { PaxFacade } from '@pax/pax.facade';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { InputDateComponent } from '@forms/form-components/components/input-date/input-date.component';

@Injectable()
export class PaxFormService {

  constructor(
    private facade: PaxFacade,
    private paxFormDocumentService: PaxFormDocumentService,
    private paxFormLoyaltyService: PaxFormLoyaltyService,
    private paxFormDateService: PaxFormDateService,
    private paxFormDataService: PaxFormDataService,
    private scrollFormService: ScrollFormService
  ) { }

  public getPaxRegisterDocuments$(paxId: string): Observable<PaxDocumentResponse[]> {
    return this.facade.travelers$.pipe(map(travelers => 
      travelers?.find(traveler => traveler._id === paxId)?.documents.filter(doc => !!doc.number && !!doc.type) || []));
  }

  public isBuses(travelerBookingProvider: TravelBookingType): boolean {
    return this.paxFormDataService.isBuses(travelerBookingProvider);
  }

  public isStaticTravelerId(id: string): boolean {
    return this.paxFormDataService.isStaticTravelerId(id);
  }

  public hasPaxTravelerInfo(paxTraveler: TravelerPaxResponse): boolean {
    return this.paxFormDataService.hasPaxTravelerInfo(paxTraveler);
  }

  public hasUserInfo(user: User, travelerNumber: number, type: TravelerTypes): boolean {
    return this.paxFormDataService.hasUserInfo(user, travelerNumber, type);
  }

  public hasPassportRestriction(isAnInternationalTrip: boolean, businessRules: BusinessRulesByBookingType): boolean {
    return this.paxFormDocumentService.hasPassportRestriction(isAnInternationalTrip, businessRules);
  }

  public addDocumentRestriction(form: UntypedFormGroup, value: boolean): void {
    this.paxFormDocumentService.addDocumentRestriction(form, value);
  }

  public defaultAirline(validator: AirValidationResponse): string {
    return this.paxFormLoyaltyService.defaultAirline(validator);
  }

  public defaultAirlineProgram(validator: AirValidationResponse, programs: FrequentFlyerResponse[] = []): string {
    return this.paxFormLoyaltyService.defaultAirlineProgram(validator, programs);
  }

  public defaultDate(paxTraveler: TravelerPaxResponse, user: User, travelerNumber: number, type: TravelerTypes): string[] {
    return this.paxFormDateService.defaultDate(paxTraveler, user, travelerNumber, type);
  }

  public getAllPaxErrors(form: UntypedFormGroup): FormErrors[] {
    if (form) {
      const errorArray = Object.keys(form.controls).map(control => {
        const errors: ValidationErrors = form.get(control).errors;
        if (errors) {
          return Object.keys(errors).map(error => {
            if (typeof errors[error] === 'boolean') {
              return this.mapPaxError(control, error);
            } else {
              return null;
            }
          }).filter(error => !!error);
        } else return null;
      });
      const response = [].concat(...errorArray.filter(error => !!error));
      return uniqWith(response, isEqual);
    } else return [];
  }

  public defaultFormData(
    paxTraveler: TravelerPaxResponse, user: User, travelerType: TravelerTypes,
    travelerNumber: number, countries: CountriesVm[], documents: BookingPaxTypeDocumentProps[],
    hasUserTraveler: number,
    validator: AirValidationResponse = null): TravelerForm {
    return this.paxFormDataService.defaultFormData(paxTraveler, user, travelerType, travelerNumber,
      countries, documents, hasUserTraveler, validator);
  }

  public formControls(
    defaultFormData: TravelerForm,
    reservationDate: Date,
    travelerType: TravelerTypes,
    travelerInfoType: TravelerInfoType,
    travelerBookingProvider: TravelBookingType,
    disabledDocuments: boolean,
    bookingSource: HotelProvider,
    documents: BookingPaxTypeDocumentProps[],
    travelerAge: number | null = null
  ): { [key: string]: UntypedFormControl } {
    return this.paxFormDataService.formControls(
      defaultFormData,
      reservationDate,
      travelerType,
      travelerInfoType,
      travelerBookingProvider,
      disabledDocuments,
      bookingSource,
      documents,
      travelerAge
    );
  }

  public changeUpperCaseName(e: Event, type: string, form: UntypedFormGroup): void {
    this.paxFormDataService.changeUpperCaseName(e, type, form);
  }

  public deleteFormData(
    form: UntypedFormGroup, defaultFormData: TravelerForm, documentSelectorRef: FormDocumentInputComponent,
    dobInputRef: InputDateComponent, documents: BookingPaxTypeDocumentProps[]): void {
    this.paxFormDataService.deleteFormData(form, defaultFormData, documentSelectorRef,
      dobInputRef, documents);
  }

  public mapperPaxData(values: TravelerForm): TravelerPax {
    return this.paxFormDataService.mapperPaxData(values);
  }

  public getLoyaltyPax(paxTraveler: TravelerPaxResponse): LoyaltyResponse {
    return this.paxFormLoyaltyService.getLoyaltyPax(paxTraveler);
  }

  public typePaxIcon(type: TravelerTypes): string {
    return type === TravelerTypes.ADULTS ? 'icon-person-outline' : 'icon-children';
  }

  public scrollIntoView(elementRef: ElementRef): void {
    this.scrollFormService.scrollIntoView(elementRef);
  }

  public validateSectionFormToScroll(form: UntypedFormGroup, refs: { [key: string]: ElementRef } = {}): void {
    if (this.validateSectionForm([TRAVELER_FORM_INPUTS.FIRST_NAME, TRAVELER_FORM_INPUTS.SECOND_NAME], form)) {
      this.scrollIntoView(refs[TRAVELER_FORM_INPUTS.FIRST_NAME]); return;
    } else if (this.validateSectionForm([TRAVELER_FORM_INPUTS.LAST_NAME], form)) {
      this.scrollIntoView(refs[TRAVELER_FORM_INPUTS.LAST_NAME]); return;
    } else if (this.validateSectionForm([TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER, TRAVELER_FORM_INPUTS.DOCUMENT_ID], form)) {
      this.scrollIntoView(refs[TRAVELER_FORM_INPUTS.DOCUMENT_ID]); return;
    } else if (form.get(TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED) &&
      this.validateSectionForm([TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED], form)) {
      this.scrollIntoView(refs[TRAVELER_FORM_INPUTS.DOCUMENT_ID]); return;
    } else if (this.validateSectionForm([TRAVELER_FORM_INPUTS.NATIONALITY], form)) {
      this.scrollIntoView(refs[TRAVELER_FORM_INPUTS.NATIONALITY]); return;
    } else if (this.validateSectionForm([TRAVELER_FORM_INPUTS.GENDER], form)) {
      this.scrollIntoView(refs[TRAVELER_FORM_INPUTS.GENDER]); return;
    } else if (this.validateSectionForm([TRAVELER_FORM_INPUTS.DOB], form)) {
      this.scrollIntoView(refs[TRAVELER_FORM_INPUTS.DOB]); return;
    }
  }

  private validateSectionForm(controls: string[], form: UntypedFormGroup): boolean {
    return controls.map(control => form.controls[control]?.status === INVALID).filter(result => result).length > 0;
  }

  private mapPaxError(control: string, error: string): FormErrors {
    switch (control) {
      case TRAVELER_FORM_INPUTS.DOCUMENT_ID:
      case TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER:
        return { [BookingFormGeneralErrorTypes.DOCUMENT_ERROR]: true };
      case TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED:
        return { [BookingFormGeneralErrorTypes.PASSPORT_RESTRICTION]: true };
      case TRAVELER_FORM_INPUTS.BONVOY_NUMBER:
        return { [BookingFormGeneralErrorTypes.INVALID_BONVOY_NUMBER]: true };
      default:
        return { [error]: true };
    }
  }
}
